/* hs-eslint ignored failing-rules */

/* eslint-disable @typescript-eslint/no-floating-promises */
import { BASE_SERVICE_URI } from '../../constants/ApiConstants';
import { dataFetchingClient, registerMutation, registerQuery, useMutation, useQuery } from '../../utils/DataFetchingClient';
import { useCallback } from 'react';
import { getQueryFieldName, queryDataFromCache, updateCache } from '../../utils/DataFetchingClientCache';
import { useUserInfo } from '../accounts';
import { http } from '../http';
import { create } from 'zustand';
import produce from 'immer';
import getLang from 'I18n/utils/getLang';
import Raven from 'raven-js';
export const PLAYLISTS_API = `${BASE_SERVICE_URI}/playlists/v1`;
export const MY_SAVED_PLAYLISTS_URL = `${PLAYLISTS_API}/user/saved`;
export const ARCHIVED_PLAYLISTS_URL = `${PLAYLISTS_API}/archived`;
export const PLAYLISTS_ITEMS_URL_FRAGMENT = `/items`;
const GET_PLAYLIST = registerQuery({
  fieldName: 'playlist',
  args: ['playlistId'],

  fetcher({
    playlistId
  }) {
    return http.get(`${PLAYLISTS_API}/${playlistId}`);
  }

});

const addPlaylistsToCache = playlists => {
  playlists.forEach(playlist => {
    const variables = {
      playlistId: playlist.id
    };

    if (!queryDataFromCache(GET_PLAYLIST, variables)) {
      updateCache(GET_PLAYLIST, () => playlist, variables);
    }
  });
};

const updatePlaylistFromCache = (playlistId, playlist) => {
  const variables = {
    playlistId
  };
  const currentPlaylist = queryDataFromCache(GET_PLAYLIST, variables);

  if (currentPlaylist) {
    const updatedPlaylist = playlist ? Object.assign({}, currentPlaylist, {}, playlist) : null;
    updateCache(GET_PLAYLIST, () => updatedPlaylist, variables);
  }
};

const FETCH_MY_PLAYLISTS = registerQuery({
  fieldName: 'myPlaylists',
  args: ['offset', 'limit', 'includeSaved'],

  fetcher({
    offset,
    limit,
    includeSaved
  }) {
    return http.get(PLAYLISTS_API, {
      query: {
        offset,
        limit,
        includeSaved
      }
    }).then(result => {
      if (!result) return result;
      addPlaylistsToCache(result.results);
      return result;
    });
  }

});
export const useMyPlaylists = ({
  includeSaved = true,
  limit,
  page,
  skip = false
} = {}) => {
  const {
    data: playlistsResponse,
    loading: playlistsLoading,
    error: playlistsError
  } = useQuery(FETCH_MY_PLAYLISTS, {
    skip,
    variables: {
      offset: limit && page ? `${limit * (page - 1)}` : undefined,
      limit: limit ? `${limit}` : undefined,
      includeSaved
    }
  });
  return {
    totalPlaylists: playlistsResponse ? playlistsResponse.total : 0,
    offset: playlistsResponse ? playlistsResponse.offset : 0,
    hasMore: playlistsResponse ? playlistsResponse.hasMore : false,
    playlists: playlistsResponse ? playlistsResponse.results : null,
    playlistsLoading,
    playlistsError
  };
};
const FETCH_ARCHIVED_PLAYLISTS = registerQuery({
  fieldName: 'archivedPlaylists',
  args: ['offset', 'limit', 'includeSaved'],

  fetcher({
    offset,
    limit,
    includeSaved
  }) {
    return http.get(ARCHIVED_PLAYLISTS_URL, {
      query: {
        offset,
        limit,
        includeSaved
      }
    }).then(result => {
      if (!result) return result;
      addPlaylistsToCache(result.results);
      return result;
    });
  }

});
export const useArchivedPlaylists = ({
  includeSaved = true,
  limit,
  page,
  skip = false
} = {}) => {
  const {
    data: archivedPlaylistsResponse,
    loading: archivedPlaylistsLoading,
    error: archivedPlaylistsError
  } = useQuery(FETCH_ARCHIVED_PLAYLISTS, {
    skip,
    variables: {
      offset: limit && page ? `${limit * (page - 1)}` : undefined,
      limit: limit ? `${limit}` : undefined,
      includeSaved
    }
  });
  return {
    totalArchivedPlaylists: archivedPlaylistsResponse ? archivedPlaylistsResponse.total : 0,
    offset: archivedPlaylistsResponse ? archivedPlaylistsResponse.offset : 0,
    hasMore: archivedPlaylistsResponse ? archivedPlaylistsResponse.hasMore : false,
    archivedPlaylists: archivedPlaylistsResponse ? archivedPlaylistsResponse.results : null,
    archivedPlaylistsLoading,
    archivedPlaylistsError
  };
};
export const usePlaylist = (playlistId, skip = false) => {
  const {
    data: playlist,
    loading: playlistLoading,
    error: playlistError
  } = useQuery(GET_PLAYLIST, {
    variables: {
      playlistId
    },
    skip
  });
  return {
    playlist,
    playlistLoading,
    playlistError
  };
}; // the difference between a full playlist and a basic one is that the basic playlist
// has an empty list of items. The way to know if it is a full playlist

const isFullPlaylist = cachePlaylist => cachePlaylist && cachePlaylist.items && cachePlaylist.itemsCount === cachePlaylist.items.length;

export const useFullPlaylist = (playlistId, skip) => {
  const cachePlaylist = queryDataFromCache(GET_PLAYLIST, {
    playlistId
  });
  const {
    data: playlist,
    loading: playlistLoading,
    error: playlistError
  } = useQuery(GET_PLAYLIST, {
    variables: {
      playlistId
    },
    // in case we have the basic playlist (no items) in cache or it has not been cached
    // we want to request the full playlist and update the cache with it
    fetchPolicy: isFullPlaylist(cachePlaylist) ? 'cache-only' : 'network-only',
    skip
  });
  return {
    playlist,
    playlistLoading,
    playlistError
  };
};
const CREATE_PLAYLIST = registerMutation({
  fieldName: 'createPlaylist',
  args: ['name', 'description'],

  fetcher({
    name,
    description,
    items
  }) {
    return http.post(PLAYLISTS_API, {
      data: {
        name,
        description,
        items
      },
      query: {
        saveWhenCreated: true
      }
    });
  }

});
export const useCreatePlaylist = ({
  onCompleted
} = {}) => {
  const [mutate, {
    loading: createPlaylistLoading,
    error: createPlaylistError
  }] = useMutation(CREATE_PLAYLIST, {
    onCompleted: playlistRecord => {
      if (onCompleted) onCompleted(playlistRecord.createPlaylist); // refetch doesn't work if we add an item to the recently created playlist.
      // Using manuad eviction as an alternative

      dataFetchingClient.refetchQueries({
        updateCache(cache) {
          cache.evict({
            fieldName: getQueryFieldName(FETCH_MY_PLAYLISTS)
          });
        }

      });
    }
  });
  const createPlaylist = useCallback(({
    name,
    description,
    items
  }) => {
    return mutate({
      variables: {
        name,
        description,
        items
      }
    }).catch(error => {
      Raven.captureException(error);
    });
  }, [mutate]);
  return {
    createPlaylist,
    createPlaylistLoading,
    createPlaylistError
  };
};
export const useLatestSavedItemsInteractions = create(set => ({
  latestInteraction: null,
  setLatestInteraction: interaction => set(produce(state => {
    state.latestInteraction = interaction;
  })),
  removeLatestInteraction: () => set(produce(state => {
    state.latestInteraction = null;
  }))
}));
export const SAVE_PLAYLIST = registerMutation({
  fieldName: 'savePlaylist',
  args: ['playlistId'],

  fetcher({
    playlistId
  }) {
    return http.post(`${MY_SAVED_PLAYLISTS_URL}`, {
      data: {
        playlistId
      }
    });
  }

});
export const useSavePlaylist = (playlistId, {
  showNotification = true,
  language = getLang()
}) => {
  const {
    setLatestInteraction
  } = useLatestSavedItemsInteractions();
  const [mutate, {
    loading: savePlaylistMutationLoading,
    called: savePlaylistMutationCalled,
    error: savePlaylistMutationError
  }] = useMutation(SAVE_PLAYLIST, {
    variables: {
      playlistId
    },
    refetchQueries: [FETCH_MY_PLAYLISTS],
    update: () => {
      updatePlaylistFromCache(playlistId, {
        savedByUser: true
      });
      if (showNotification) setLatestInteraction({
        playlistId,
        language,
        action: 'SAVE_PLAYLIST'
      });
    }
  });
  return [mutate, {
    savePlaylistMutationLoading,
    savePlaylistMutationCalled,
    savePlaylistMutationError
  }];
};
export const UNSAVE_PLAYLIST = registerMutation({
  fieldName: 'unsavePlaylist',
  args: ['playlistId'],

  fetcher({
    playlistId
  }) {
    return http.delete(`${MY_SAVED_PLAYLISTS_URL}/${playlistId}`);
  }

});
export const useUnsavePlaylist = (playlistId, {
  showNotification = true,
  language = getLang()
}) => {
  const {
    setLatestInteraction
  } = useLatestSavedItemsInteractions();
  const [mutate, {
    loading: unsavePlaylistMutationLoading,
    called: unsavePlaylistMutationCalled,
    error: unsavePlaylistMutationError
  }] = useMutation(UNSAVE_PLAYLIST, {
    variables: {
      playlistId
    },
    refetchQueries: [FETCH_MY_PLAYLISTS],
    update: () => {
      updatePlaylistFromCache(playlistId, {
        savedByUser: false
      });
      if (showNotification) setLatestInteraction({
        playlistId,
        language,
        action: 'UNSAVE_PLAYLIST'
      });
    }
  });
  return [mutate, {
    unsavePlaylistMutationLoading,
    unsavePlaylistMutationCalled,
    unsavePlaylistMutationError
  }];
};
export const UPDATE_PLAYLIST = registerMutation({
  fieldName: 'updatePlaylist',
  args: ['playlistId', 'playlist'],

  fetcher({
    playlistId,
    playlist
  }) {
    if (playlist) return http.put(`${PLAYLISTS_API}/${playlistId}`, {
      data: playlist
    });else throw new Error(`Playlist ${playlistId} not loaded`);
  }

});
export const useUpdatePlaylist = (playlistId, {
  onCompleted,
  refetchQueries,
  onError
} = {
  onCompleted: () => {},
  onError: () => {},
  refetchQueries: []
}) => {
  const {
    playlist
  } = usePlaylist(playlistId);
  const [mutate, {
    loading: updatePlaylistMutationLoading,
    called: updatePlaylistMutationCalled,
    error: updatePlaylistMutationError
  }] = useMutation(UPDATE_PLAYLIST, {
    refetchQueries,
    onCompleted,
    onError,

    update(cache, result) {
      const updatedPlaylist = result.data && result.data.updatePlaylist;

      if (updatedPlaylist) {
        updatePlaylistFromCache(updatedPlaylist.id, updatedPlaylist);
      }
    }

  });
  const updatePlaylist = useCallback((updatedPlaylist, opts = {}) => {
    const newPlaylist = Object.assign({}, playlist, {}, updatedPlaylist);
    return mutate({
      variables: {
        playlistId,
        playlist: newPlaylist
      },
      optimisticResponse: opts.optimisticResponse ? {
        updatePlaylist: newPlaylist
      } : undefined
    }).catch(error => {
      Raven.captureException(error);
    });
  }, [mutate, playlist, playlistId]);
  return [updatePlaylist, {
    updatePlaylistMutationLoading,
    updatePlaylistMutationCalled,
    updatePlaylistMutationError
  }];
};
export const useSetHiddenForOwner = (playlistId, hiddenForOwner, {
  showNotification = true,
  language = getLang()
}) => {
  const {
    setLatestInteraction
  } = useLatestSavedItemsInteractions();

  const onCompleted = ({
    updatePlaylist
  }) => {
    // Unfortunately refetch has a bug and doesn't keep track of query usages
    // in other components and doesn't refetch them. This was the proposed
    // solution to solve the refetching problem.
    dataFetchingClient.refetchQueries({
      updateCache(cache) {
        cache.evict({
          fieldName: getQueryFieldName(FETCH_ARCHIVED_PLAYLISTS)
        });
        cache.evict({
          fieldName: getQueryFieldName(FETCH_MY_PLAYLISTS)
        });
      }

    });
    if (showNotification) setLatestInteraction({
      playlistId,
      language,
      action: updatePlaylist.hiddenForOwner ? 'ARCHIVE_PLAYLIST' : 'UNARCHIVE_PLAYLIST'
    });
  };

  const [mutate, {
    updatePlaylistMutationLoading: archievePlaylistMutationLoading,
    updatePlaylistMutationCalled: archievePlaylistMutationCalled,
    updatePlaylistMutationError: archievePlaylistMutationError
  }] = useUpdatePlaylist(playlistId, {
    onCompleted
  });
  const setHiddenForOwner = useCallback(() => {
    return mutate({
      hiddenForOwner
    });
  }, [hiddenForOwner, mutate]);
  return [setHiddenForOwner, {
    archievePlaylistMutationLoading,
    archievePlaylistMutationCalled,
    archievePlaylistMutationError
  }];
};
export const ADD_TO_PLAYLIST = registerMutation({
  fieldName: 'addToPlaylist',
  args: ['playlistId', 'itemId', 'type', 'language'],

  fetcher({
    playlistId,
    itemId,
    type,
    language
  }) {
    return http.post(`${PLAYLISTS_API}/${playlistId}${PLAYLISTS_ITEMS_URL_FRAGMENT}`, {
      data: {
        itemId,
        type,
        language
      }
    });
  }

});
export const REMOVE_FROM_PLAYLIST = registerMutation({
  fieldName: 'addToPlaylist',
  args: ['playlistId', 'itemId', 'type', 'language'],

  fetcher({
    playlistId,
    itemId,
    type
  }) {
    return http.delete(`${PLAYLISTS_API}/${playlistId}${PLAYLISTS_ITEMS_URL_FRAGMENT}/${itemId}/${type}`);
  }

});
export const useAddToPlaylist = ({
  showNotification = true
} = {}) => {
  const {
    setLatestInteraction
  } = useLatestSavedItemsInteractions();
  const [mutate, {
    loading: addToPlaylistMutationLoading,
    called: addToPlaylistMutationCalled,
    error: addToPlaylistMutationError
  }] = useMutation(ADD_TO_PLAYLIST, {
    update: (cache, {
      data,
      errors
    }, {
      variables
    }) => {
      if (!data || !variables || errors && errors.length) return;
      updatePlaylistFromCache(data.addToPlaylist.id, data.addToPlaylist);
      if (showNotification) setLatestInteraction(Object.assign({}, variables, {
        action: 'ADD_TO_PLAYLIST'
      }));
    }
  });
  const addToPlaylist = useCallback(({
    playlistId,
    itemId,
    type,
    language
  }) => {
    return mutate({
      variables: {
        playlistId: Number(playlistId),
        itemId: Number(itemId),
        type,
        language: language.toLowerCase()
      }
    });
  }, [mutate]);
  return [addToPlaylist, {
    addToPlaylistMutationLoading,
    addToPlaylistMutationCalled,
    addToPlaylistMutationError
  }];
};
export const useRemoveFromPlaylist = ({
  showNotification = true
} = {}) => {
  const {
    setLatestInteraction
  } = useLatestSavedItemsInteractions();
  const [mutate, {
    loading: removeFromPlaylistMutationLoading,
    called: removeFromPlaylistMutationCalled,
    error: removeFromPlaylistMutationError
  }] = useMutation(REMOVE_FROM_PLAYLIST, {
    update: (cache, {
      data,
      errors
    }, {
      variables
    }) => {
      if (!data || !variables || errors && errors.length) return;
      updatePlaylistFromCache(data.addToPlaylist.id, data.addToPlaylist);
      if (showNotification) setLatestInteraction(Object.assign({}, variables, {
        action: 'REMOVE_FROM_PLAYLIST'
      }));
    }
  });
  const removeFromPlaylist = useCallback(({
    playlistId,
    itemId,
    type,
    language
  }) => {
    return mutate({
      variables: {
        playlistId: Number(playlistId),
        itemId: Number(itemId),
        type,
        language: language.toLowerCase()
      }
    });
  }, [mutate]);
  return [removeFromPlaylist, {
    removeFromPlaylistMutationLoading,
    removeFromPlaylistMutationCalled,
    removeFromPlaylistMutationError
  }];
};
export const useUserOwnsPlaylist = playlistId => {
  const {
    userInfo
  } = useUserInfo();
  const {
    playlist
  } = usePlaylist(playlistId, !playlistId);
  if (!userInfo || !playlist) return null;
  return playlist.ownerUserId === userInfo.user.id;
};
export const useSetPlaylistThumbnail = playlistId => {
  const [mutate, {
    updatePlaylistMutationLoading: setPlaylistThumbnailMutationLoading,
    updatePlaylistMutationCalled: setPlaylistThumbnailMutationCalled,
    updatePlaylistMutationError: setPlaylistThumbnailMutationError
  }] = useUpdatePlaylist(playlistId);
  const setPlaylistThumbnail = useCallback((thumbnailRectangleUrl, thumbnailSquareUrl) => {
    return mutate({
      thumbnailRectangleUrl,
      thumbnailSquareUrl
    });
  }, [mutate]);
  return [setPlaylistThumbnail, {
    setPlaylistThumbnailMutationLoading,
    setPlaylistThumbnailMutationCalled,
    setPlaylistThumbnailMutationError
  }];
};